import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { LoadingButton } from "~/components/loading-button";
import { SelectMenu } from "~/components/Form/components/SelectMenu/SelectMenu";
import { Option } from "~/components/Form/components/SelectMenu/SelectMenu";
import { MembersRolesList } from "~/components/Members/MembersRolesList";
import {
  BASE_MEMBERS_ROLES,
  GROUPED_MEMBERS_ROLES,
  PERMISSIONS,
} from "~/components/Members/const";
import { hasAnyBaseRoleSelected } from "~/components/Members/utils/membersUtils";
import { roleOrgMember } from "~/lib/iam/roles/OrgMember";

type FormValues = {
  selectedRoles: string[];
};

export type EditOrganizationalRoleDialogProps = {
  open: boolean;
  currentRoles: Array<{ mrn: string; title?: string }>;
  onClose: () => void;
  onSave: (roles: Array<{ mrn: string }>) => void;
  isLoading?: boolean;
};

export function EditOrganizationalRoleDialog({
  open,
  currentRoles,
  onClose,
  onSave,
  isLoading = false,
}: EditOrganizationalRoleDialogProps) {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    watch,
    setValue,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      selectedRoles: currentRoles
        .map((role) => role.mrn)
        .filter((mrn) => mrn !== roleOrgMember.mrn),
    },
    mode: "onChange",
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [isRolesDropdownOpen, setIsRolesDropdownOpen] = useState(false);
  const watchSelectedRoles = watch("selectedRoles");

  const onSubmit = (data: FormValues) => {
    onSave(data.selectedRoles.map((role) => ({ mrn: role })));
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: "background.default",
            position: "relative",
            overflow: "visible",
          },
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Edit Organizational Role
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflow: "visible" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select the organizational roles for this user. Base roles define broad
          access levels, while detailed permissions provide granular control.
        </Typography>

        <Button
          variant="text"
          color="primary"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
            setIsRolesDropdownOpen(true);
          }}
          startIcon={<AddIcon />}
          data-testid="add-roles-button"
          sx={{ mb: 2 }}
        >
          Add Roles
        </Button>

        <FormHelperText error>
          {errors.selectedRoles?.message || ""}
        </FormHelperText>

        <Controller
          name="selectedRoles"
          control={control}
          rules={{
            validate: (value) => {
              const isHasAnyBaseRoleSelected = hasAnyBaseRoleSelected(value);
              if (!isHasAnyBaseRoleSelected) {
                return "Must have at least one base role selected";
              }
              return true;
            },
          }}
          render={({ field, formState: { errors } }) => (
            <Box sx={{ position: "relative" }}>
              <SelectMenu
                isOpen={isRolesDropdownOpen}
                anchorRef={anchorEl}
                onClose={() => {
                  setAnchorEl(null);
                  setIsRolesDropdownOpen(false);
                }}
                maxHeight={208}
                options={GROUPED_MEMBERS_ROLES}
                onChange={(value) => {
                  field.onChange(value);
                }}
                multiple={true}
                value={field.value}
                variant="checkbox"
                error={!!errors.selectedRoles}
                helperText={errors.selectedRoles?.message || ""}
              />
            </Box>
          )}
        />

        <Box mt={3}>
          <MembersRolesList
            roles={watchSelectedRoles
              .map((roleMrn) => {
                const label = [...BASE_MEMBERS_ROLES, ...PERMISSIONS].find(
                  (role) => role.value === roleMrn,
                )?.label;

                return label ? { value: roleMrn, label } : null;
              })
              .filter((option): option is Option => option !== null)}
            onDelete={(roleMrn) => {
              setValue(
                "selectedRoles",
                watchSelectedRoles.filter((x) => x !== roleMrn),
                { shouldValidate: true, shouldDirty: true },
              );
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "flex-end",
          px: 3,
          py: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={isLoading}
          buttonText="Save"
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading || !isDirty || !isValid}
          sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
        />
      </DialogActions>
    </Dialog>
  );
}
